import {
  addDays,
  addMinutes,
  eachDayOfInterval,
  format,
  getDay,
  getHours,
  getMinutes,
  isSameDay,
  parseISO,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useMemo } from "react";

import {
  AvailabilityItem,
  MAX_DAYS_TO_DISPLAY,
  StaffDetails,
  TimeOfDay,
} from "@smart/bridge-types-basic";
import { groupBy } from "@smart/itops-utils-basic";

import { APPT_DEFAULT_DURATION, maxTimeOfDay, minTimeOfDay } from "../types";

export const formatTimeOfDay = (time: TimeOfDay): string => {
  const hour = time.hour % 12 || 12;
  const minute = time.minute.toString().padStart(2, "0");
  const period = time.hour < 12 ? "AM" : "PM";

  return `${hour}:${minute} ${period}`;
};

export const timeOfDayInMinutes = (time: TimeOfDay): number =>
  time.hour * 60 + time.minute;

export const toTimeOfDay = (minutes: number): TimeOfDay => ({
  hour: Math.floor(minutes / 60),
  minute: minutes % 60,
});

export const buildTimeOfDayRange = (options?: {
  start?: TimeOfDay;
  end?: TimeOfDay;
  duration?: number;
}): TimeOfDay[] => {
  const startInMinutes = timeOfDayInMinutes(options?.start || minTimeOfDay);
  const endInMinutes = timeOfDayInMinutes(options?.end || maxTimeOfDay);
  if (startInMinutes > endInMinutes) return [];

  const range = [];

  const duration = options?.duration || APPT_DEFAULT_DURATION;
  let current = startInMinutes;
  while (current <= endInMinutes) {
    range.push(toTimeOfDay(current));
    current += duration;
  }

  return range;
};

export const getDateString = (date: Date) => format(date, "yyyy-MM-dd");

export const convertToZonedTime = (date: Date, timezone?: string | null) =>
  timezone ? toZonedTime(date, timezone) : date;

type CalculateAvailableTimeSlotsOptions = {
  duration: number;
  availability: AvailabilityItem[];
  creationTimezone?: string;
  blocked: {
    fromTime: string;
    toTime: string;
    timezone?: string | null;
  }[];
  viewingTimezone: string;
  minimumNoticeInMinutes?: number;
  bufferTimeInMinutes?: number;
  numberOfDisplayDays: number;
};

const generateAvailableSlotsForDay = ({
  availability,
  dayInCreationTimezone,
  durationInMinute,
  blockedInCreationTimezone,
  bufferTimeInMinutes,
  minimumNoticeInMinutes,
  creationTimezone,
}: {
  availability: AvailabilityItem[];
  dayInCreationTimezone: Date;
  durationInMinute: number;
  blockedInCreationTimezone: {
    fromDateTime: Date;
    toDateTime: Date;
  }[];
  bufferTimeInMinutes?: number;
  minimumNoticeInMinutes?: number;
  creationTimezone?: string;
}) => {
  const dayAvailability = availability.find(
    (a) => a.day === getDay(dayInCreationTimezone) && a.enabled,
  );
  if (!dayAvailability) return [];

  const blockedInCreationTimezoneWithBuffer = blockedInCreationTimezone
    .filter(
      (b) =>
        isSameDay(dayInCreationTimezone, b.fromDateTime) ||
        isSameDay(dayInCreationTimezone, b.toDateTime) ||
        (b.fromDateTime.getTime() < dayInCreationTimezone.getTime() &&
          b.toDateTime.getTime() > dayInCreationTimezone.getTime()),
    )
    .map((b) => ({
      fromDateTime: addMinutes(b.fromDateTime, -(bufferTimeInMinutes || 0)),
      toDateTime: addMinutes(b.toDateTime, bufferTimeInMinutes || 0),
    }));

  const isBlocked = ({
    startTime,
    duration,
    blockedDateTimes,
  }: {
    startTime: Date;
    duration: number;
    blockedDateTimes: { fromDateTime: Date; toDateTime: Date }[];
  }): boolean =>
    blockedDateTimes.length > 0 &&
    blockedDateTimes.some(
      (b) =>
        startTime.getTime() < b.toDateTime.getTime() &&
        addMinutes(startTime, duration).getTime() > b.fromDateTime.getTime(),
    );

  const createZonedTime = (timeOfDay: TimeOfDay) => {
    const copied = new Date(dayInCreationTimezone.getTime());
    copied.setHours(timeOfDay.hour);
    copied.setMinutes(timeOfDay.minute);
    return copied;
  };

  const fromTime = createZonedTime(dayAvailability.fromTime);
  const toTime = createZonedTime(dayAvailability.toTime);
  const earliestAvailable = addMinutes(new Date(), minimumNoticeInMinutes || 0);
  const earliestAvailableInCreationTimezone = convertToZonedTime(
    earliestAvailable,
    creationTimezone,
  );

  const slots = [];
  let currentTime = fromTime;

  while (
    toTime.getTime() - currentTime.getTime() >=
    durationInMinute * 60 * 1000
  ) {
    const nextTime = addMinutes(currentTime, durationInMinute);
    if (
      currentTime.getTime() >= earliestAvailableInCreationTimezone.getTime() &&
      !isBlocked({
        startTime: currentTime,
        duration: durationInMinute,
        blockedDateTimes: blockedInCreationTimezoneWithBuffer,
      })
    ) {
      slots.push(currentTime);
    }
    currentTime = nextTime;
  }

  return slots;
};

const calculateAvailableTimeSlots = (
  options: CalculateAvailableTimeSlotsOptions,
): Record<string, TimeOfDay[]> => {
  const {
    availability,
    viewingTimezone,
    creationTimezone,
    duration,
    blocked,
    bufferTimeInMinutes,
    minimumNoticeInMinutes,
    numberOfDisplayDays,
  } = options;

  // If creationTimezone is not set, it's considered in UTC
  const startDateInCreationTimezone = convertToZonedTime(
    new Date(),
    creationTimezone,
  );

  const blockedInCreationTimezone = blocked.map((b) => {
    const utcFrom = fromZonedTime(b.fromTime, b.timezone || "UTC");
    const utcTo = fromZonedTime(b.toTime, b.timezone || "UTC");

    return {
      fromDateTime: convertToZonedTime(utcFrom, creationTimezone),
      toDateTime: convertToZonedTime(utcTo, creationTimezone),
    };
  });

  const slotsInCreationTimezone: Date[][] = [];
  let currentDate = startDateInCreationTimezone;

  /**
   * Generate available slots for numberOfDisplayDays calendar days into the future,
   * starting from "today" in the creation timezone.
   */
  Array.from({ length: numberOfDisplayDays }, () => {
    const slots = generateAvailableSlotsForDay({
      availability,
      dayInCreationTimezone: currentDate,
      durationInMinute: duration,
      blockedInCreationTimezone,
      bufferTimeInMinutes,
      minimumNoticeInMinutes,
      creationTimezone,
    });

    if (slots.length) {
      slotsInCreationTimezone.push(slots);
    }
    currentDate = addDays(currentDate, 1);

    return undefined;
  });

  const slotsInViewingTimezone = slotsInCreationTimezone
    .flat()
    .map((startTimeInCreationTimezone) => {
      const utcStartTime = creationTimezone
        ? fromZonedTime(startTimeInCreationTimezone, creationTimezone)
        : startTimeInCreationTimezone;
      return convertToZonedTime(utcStartTime, viewingTimezone);
    });
  const groupedSlotsInViewingTimezone = groupBy(
    slotsInViewingTimezone,
    (slot) => getDateString(slot),
  );

  return Object.keys(groupedSlotsInViewingTimezone).reduce(
    (aggr, date) => ({
      ...aggr,
      [date]: groupedSlotsInViewingTimezone[date].map(
        (startTimeInViewingTimezone) => ({
          hour: getHours(startTimeInViewingTimezone),
          minute: getMinutes(startTimeInViewingTimezone),
        }),
      ),
    }),
    {},
  );
};

export const useAvailableSlots = (
  options: CalculateAvailableTimeSlotsOptions & {
    selectedDateInViewingTimezone: string | null | undefined;
    deps: any[];
  },
) => {
  const { selectedDateInViewingTimezone, deps, ...rest } = options;
  const availableSlotsInViewingTimezone = useMemo(
    () => calculateAvailableTimeSlots(rest),
    [...deps],
  );

  const { startDate, toDate, excludingDates } = useMemo(() => {
    /**
     * parseISO function will create the date with local timezone.
     * This means if the date is formatted using the format function
     * from date-fns, the result will always be the same date.
     * E.g, format(parseISO("2025-01-31"), "yyyy-MM-dd") will always be "2025-01-31".
     * If we use the new Date() method: format(new Date("2025-01-31"), "yyyy-MM-dd"),
     * the result may vary based on your local timezone being behind or ahead of UTC time
     * since new Date("2025-01-31") will create a date object with UTC time 00:00:00 on 2025-01-31.
     */
    const orderedDates = Object.keys(availableSlotsInViewingTimezone)
      .map((dateString) => parseISO(dateString))
      .sort((a, b) => a.getTime() - b.getTime());

    if (!orderedDates.length) {
      return {
        startDate: undefined,
        toDate: undefined,
        excludingDates: [],
        availableTimeSlotsForSelectedDate: undefined,
      };
    }

    const startDateString = getDateString(orderedDates[0]);
    const toDateString = getDateString(orderedDates[orderedDates.length - 1]);
    const allDatesInRange = eachDayOfInterval({
      start: orderedDates[0],
      end: orderedDates[orderedDates.length - 1],
    });
    const excludingDateStrings = allDatesInRange
      .filter((date) => !orderedDates.some((d) => isSameDay(date, d)))
      .map((date) => getDateString(date));

    return {
      startDate: startDateString,
      toDate: toDateString,
      excludingDates: excludingDateStrings,
    };
  }, [...deps]);

  return {
    startDate,
    toDate,
    excludingDates,
    availableTimeSlotsForSelectedDate: selectedDateInViewingTimezone
      ? availableSlotsInViewingTimezone[
          getDateString(parseISO(selectedDateInViewingTimezone))
        ]
      : undefined,
  };
};

export const findFirstAvailableStaff = ({
  staffDetails,
  availability,
  duration,
  creationTimezone,
  viewingTimezone,
  minimumNoticeInMinutes,
  bufferTimeInMinutes,
  numberOfDisplayDays = MAX_DAYS_TO_DISPLAY,
}: {
  staffDetails: StaffDetails[];
  availability: AvailabilityItem[];
  duration: number;
  creationTimezone?: string;
  viewingTimezone: string;
  minimumNoticeInMinutes?: number;
  bufferTimeInMinutes?: number;
  numberOfDisplayDays?: number;
}): { staffId: string; date: string; time: TimeOfDay } | null => {
  let earliest: {
    staffId: string;
    date: string;
    time: TimeOfDay;
    dateObj: Date;
    firstName?: string | null;
    lastName?: string | null;
    id: string;
  } | null = null;

  const nameCompare = (a?: string | null, b?: string | null) =>
    (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" });

  for (const staff of staffDetails) {
    const blocked = (staff.busySlots || []).map((b) => ({
      fromTime: b.startTime,
      toTime: b.endTime,
      timezone: b.timezone || undefined,
    }));

    const availableSlotsMap = calculateAvailableTimeSlots({
      availability,
      viewingTimezone,
      creationTimezone,
      duration,
      blocked,
      bufferTimeInMinutes,
      minimumNoticeInMinutes,
      numberOfDisplayDays,
    });

    const dateKeys = Object.keys(availableSlotsMap).sort();
    // eslint-disable-next-line no-continue
    if (dateKeys.length === 0) continue;

    const firstDate = dateKeys[0];
    const times = availableSlotsMap[firstDate];
    // eslint-disable-next-line no-continue
    if (!times || times.length === 0) continue;

    const firstTime = times[0];

    const dt = parseISO(firstDate);
    dt.setHours(firstTime.hour, firstTime.minute, 0, 0);

    if (!earliest) {
      earliest = {
        staffId: staff.id,
        date: firstDate,
        time: firstTime,
        dateObj: dt,
        firstName: staff.firstName ?? "",
        lastName: staff.lastName ?? "",
        id: staff.id,
      };
      // eslint-disable-next-line no-continue
      continue;
    }

    const currentMs = dt.getTime();
    const earliestMs = earliest.dateObj.getTime();

    if (currentMs < earliestMs) {
      earliest = {
        staffId: staff.id,
        date: firstDate,
        time: firstTime,
        dateObj: dt,
        firstName: staff.firstName ?? "",
        lastName: staff.lastName ?? "",
        id: staff.id,
      };
    } else if (currentMs === earliestMs) {
      // Same earliest availability: compare firstName (case-insensitive), then lastName, then id
      const compareFirst = nameCompare(staff.firstName, earliest.firstName);
      if (compareFirst < 0) {
        earliest = {
          staffId: staff.id,
          date: firstDate,
          time: firstTime,
          dateObj: dt,
          firstName: staff.firstName ?? "",
          lastName: staff.lastName ?? "",
          id: staff.id,
        };
      } else if (compareFirst === 0) {
        const compareLast = nameCompare(staff.lastName, earliest.lastName);
        if (compareLast < 0) {
          earliest = {
            staffId: staff.id,
            date: firstDate,
            time: firstTime,
            dateObj: dt,
            firstName: staff.firstName ?? "",
            lastName: staff.lastName ?? "",
            id: staff.id,
          };
        } else if (compareLast === 0) {
          if ((staff.id ?? "").localeCompare(earliest.id) < 0) {
            earliest = {
              staffId: staff.id,
              date: firstDate,
              time: firstTime,
              dateObj: dt,
              firstName: staff.firstName ?? "",
              lastName: staff.lastName ?? "",
              id: staff.id,
            };
          }
        }
      }
    }
  }

  if (!earliest) return null;

  return {
    staffId: earliest.staffId,
    date: earliest.date,
    time: earliest.time,
  };
};
